import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, Clock, Info, RefreshCw, TrendingUp, Users } from "lucide-react";
import {
  computeAvgTurnTime,
  computeAvgWaitToSeat,
  computeCancellationRate,
  computeCovers,
  computeCoversPerHour,
  computeNoShowRate,
  computeOriginMix,
  computePartySizeMix,
  computePeakHour,
  computeSeatUtilization,
  computeTableTurns,
  computeTagCounts,
  computeWalkinMix,
  formatHourLabel,
  formatMinutesAsHm,
  formatPct,
} from "@/lib/day-metrics";
import { useAppStore, useTodayReservations } from "@/store/store";
import { useOpeningHours } from "@/hooks/use-opening-hours";
import { dayjs } from "@/utils/date";

const ORIGIN_LABELS: Record<string, string> = {
  seimseim: "Staff",
  walkin: "Walk-in",
  google: "Google",
  whatsapp: "WhatsApp",
  web: "Web",
  phone: "Teléfono",
  agent: "Agente IA",
};

function Kpi({
  icon,
  label,
  value,
  hint,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  hint?: string;
  tone?: "default" | "warning" | "good";
}) {
  const tones = {
    default: "text-foreground",
    warning: "text-amber-700",
    good: "text-emerald-700",
  };
  return (
    <div className="rounded-lg border border-border bg-white p-3 flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-text-secondary uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-bold leading-tight ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-text-secondary">{hint}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-white p-4 flex flex-col gap-3 min-w-0">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

/** Service-open minutes for ``selectedDate`` based on the location's
 *  opening-hours blocks for that weekday. Used as the denominator of
 *  seat utilization. */
function useServiceOpenMinutes(selectedDate: string): number {
  const { data: blocks } = useOpeningHours();
  return useMemo(() => {
    if (!blocks) return 0;
    const weekday = (dayjs(selectedDate).day() + 6) % 7;
    const todays = blocks.filter((b) => b.day_of_week === weekday);
    let total = 0;
    for (const b of todays) {
      const [oh, om] = b.open_time.split(":").map(Number);
      const [ch, cm] = b.close_time.split(":").map(Number);
      let close = ch * 60 + cm;
      const open = oh * 60 + om;
      if (close === 0) close = 24 * 60; // "00:00" = midnight
      total += Math.max(0, close - open);
    }
    return total;
  }, [blocks, selectedDate]);
}

export default function ServiceRecapReport() {
  const reservations = useTodayReservations();
  const tables = useAppStore((s) => s.tables);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const serviceOpenMinutes = useServiceOpenMinutes(selectedDate);

  const covers = computeCovers(reservations);
  const noShowRate = computeNoShowRate(reservations);
  const cancelRate = computeCancellationRate(reservations);
  const walkin = computeWalkinMix(reservations);
  const turn = computeAvgTurnTime(reservations);
  const turns = useMemo(() => computeTableTurns(reservations), [reservations]);
  const utilization = computeSeatUtilization(reservations, tables, serviceOpenMinutes);
  const wait = computeAvgWaitToSeat(reservations);
  const peak = computePeakHour(reservations);
  const perHour = useMemo(() => computeCoversPerHour(reservations), [reservations]);
  const origin = useMemo(() => computeOriginMix(reservations), [reservations]);
  const sizeMix = useMemo(() => computePartySizeMix(reservations), [reservations]);
  const tags = useMemo(() => computeTagCounts(reservations), [reservations]);
  const dietaryTags = useMemo(() => tags.filter((t) => t.isDietary), [tags]);
  const partyTags = useMemo(() => tags.filter((t) => !t.isDietary), [tags]);

  const totalNonCancelled = reservations.filter((r) => r.status !== "cancelada").length;

  if (reservations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-text-secondary gap-2 py-12">
        <Info size={32} className="opacity-40" />
        <p className="text-sm">No hubo reservas registradas para este día.</p>
      </div>
    );
  }

  const tableNameById = new Map(tables.map((t) => [t.id, t.name]));

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6">
      <div className="mb-4">
        <h2 className="text-base font-bold">Recap del servicio</h2>
        <p className="text-xs text-text-secondary">
          Métricas operativas y financieras del servicio cerrado.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Kpi
          icon={<Users size={14} />}
          label="Covers"
          value={covers}
          hint={`${totalNonCancelled} reservas`}
        />
        <Kpi
          icon={<TrendingUp size={14} />}
          label="Tasa de no-show"
          value={formatPct(noShowRate)}
          tone={noShowRate > 0.1 ? "warning" : "default"}
        />
        <Kpi
          icon={<Activity size={14} />}
          label="Tasa de cancelación"
          value={formatPct(cancelRate)}
        />
        <Kpi
          icon={<Users size={14} />}
          label="Walk-ins (mix)"
          value={formatPct(walkin.mix)}
          hint={`${walkin.covers} covers`}
        />
        <Kpi
          icon={<Clock size={14} />}
          label="Tiempo de mesa"
          value={formatMinutesAsHm(turn.mean)}
          hint={turn.median > 0 ? `mediana ${formatMinutesAsHm(turn.median)}` : undefined}
        />
        <Kpi
          icon={<RefreshCw size={14} />}
          label="Giros por mesa"
          value={turns.avg > 0 ? turns.avg.toFixed(1) : "—"}
          hint={turns.median > 0 ? `mediana ${turns.median.toFixed(1)}` : undefined}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <Kpi
          icon={<Activity size={14} />}
          label="Ocupación de asientos"
          value={formatPct(utilization)}
          hint={serviceOpenMinutes === 0 ? "(sin horario configurado)" : undefined}
        />
        <Kpi
          icon={<Clock size={14} />}
          label="Demora al sentar"
          value={
            wait === 0 && reservations.every((r) => !r.seatedAt)
              ? "—"
              : `${wait > 0 ? "+" : ""}${wait}m`
          }
          tone={wait > 10 ? "warning" : wait < 0 ? "good" : "default"}
        />
        <Kpi
          icon={<TrendingUp size={14} />}
          label="Hora pico"
          value={peak !== null ? formatHourLabel(peak) : "—"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Covers por hora">
          {perHour.length === 0 ? (
            <p className="text-xs text-text-secondary">No hubo covers servidos.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={perHour.map((row) => ({
                    label: formatHourLabel(row.hour),
                    covers: row.covers,
                  }))}
                  margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="covers" name="Covers" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Mix por tamaño de grupo">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sizeMix} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Reservas" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Mix por canal">
          {origin.length === 0 ? (
            <p className="text-xs text-text-secondary">Sin reservas para mostrar.</p>
          ) : (
            <ul className="flex flex-col gap-1.5 text-sm">
              {origin.map((o) => (
                <li
                  key={o.origin}
                  className="flex items-center justify-between gap-3 border-b border-border/50 pb-1.5 last:border-0"
                >
                  <span className="font-medium">{ORIGIN_LABELS[o.origin] ?? o.origin}</span>
                  <span className="text-xs text-text-secondary">
                    {o.count} reservas · {o.pax} pax
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Top mesas con más giros">
          {turns.top.length === 0 ? (
            <p className="text-xs text-text-secondary">Ninguna mesa registró giros.</p>
          ) : (
            <ul className="flex flex-col gap-1.5 text-sm">
              {turns.top.map((row) => (
                <li
                  key={row.tableId}
                  className="flex items-center justify-between gap-3 border-b border-border/50 pb-1.5 last:border-0"
                >
                  <span className="font-medium">{tableNameById.get(row.tableId) ?? "Mesa"}</span>
                  <span className="text-xs text-text-secondary">{row.turns} giros</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {dietaryTags.length > 0 && (
          <Card title="Restricciones servidas">
            <ul className="flex flex-wrap gap-2">
              {dietaryTags.map((t) => (
                <li
                  key={t.tag}
                  className="text-xs px-2 py-1 rounded-md bg-surface-alt text-text-secondary"
                >
                  <span className="font-medium text-foreground">{t.label}</span> · {t.count}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {partyTags.length > 0 && (
          <Card title="En la mesa">
            <ul className="flex flex-wrap gap-2">
              {partyTags.map((t) => (
                <li
                  key={t.tag}
                  className="text-xs px-2 py-1 rounded-md bg-surface-alt text-text-secondary"
                >
                  <span className="font-medium text-foreground">{t.label}</span> · {t.count}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
